import { useCallback, useEffect, useState } from "react";
import {
  fetchCompanyUsers,
  fetchDeletedOpportunities,
  fetchDeletedTasks,
  restoreOpportunity,
} from "../../services/api/salesDeletedRecordsApi";
import { enrichOpportunitiesFunnelStageNames } from "../../services/api/metadataFunilApi";

function formatDeletedAt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return String(iso);
  }
}

function formatDeleteEndpoint(endpoint) {
  if (!endpoint || String(endpoint).trim() === "") return null;
  return String(endpoint).trim();
}

function formatDeletedBy(value) {
  if (!value || String(value).trim() === "") return "Não informado";
  return String(value).trim();
}

/** Converte valor de input datetime-local para ISO (UTC) ou undefined. */
function localDateTimeToIso(value) {
  if (value == null || String(value).trim() === "") return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * @param {{ funnelId?: string, stageId?: string }} item
 * @param {Record<string, string>} funnelNameById
 * @param {Record<string, string>} stageNameByFunnelStage chave `${funnelId}|${stageId}`
 */
function resolveFunnelStageLabels(item, funnelNameById, stageNameByFunnelStage) {
  const fid = item.funnelId != null ? String(item.funnelId).trim() : "";
  const sid = item.stageId != null ? String(item.stageId).trim() : "";
  const funnelName = fid ? funnelNameById[fid] : null;
  const stageName =
    fid && sid ? stageNameByFunnelStage[`${fid}|${sid}`] : null;
  return { fid, sid, funnelName, stageName };
}

export function DeletedOpportunitiesPanel({ companyId, companyName }) {
  const [opportunities, setOpportunities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [usersByUserId, setUsersByUserId] = useState({});
  const [title, setTitle] = useState("");
  const [fromLocal, setFromLocal] = useState("");
  const [toLocal, setToLocal] = useState("");
  const [responsibleId, setResponsibleId] = useState("");
  const [deletedBy, setDeletedBy] = useState("");
  const [taskOpportunityId, setTaskOpportunityId] = useState("");
  const [restoringId, setRestoringId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  /** @type {Record<string, string>} */
  const [funnelNameById, setFunnelNameById] = useState({});
  /** Chave `${funnelId}|${stageId}` -> nome da etapa */
  const [stageNameByFunnelStage, setStageNameByFunnelStage] = useState({});

  const buildOppFilters = useCallback(() => {
    return {
      title: title.trim() || undefined,
      fromDeletedAt: localDateTimeToIso(fromLocal),
      toDeletedAt: localDateTimeToIso(toLocal),
      responsibleId: responsibleId.trim() || undefined,
      deletedBy: deletedBy.trim() || undefined,
    };
  }, [title, fromLocal, toLocal, responsibleId, deletedBy]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const oppFilters = buildOppFilters();
      const taskFilters = {
        ...oppFilters,
        opportunityId: taskOpportunityId.trim() || undefined,
      };
      const [opps, tks, users] = await Promise.all([
        fetchDeletedOpportunities(companyId, oppFilters),
        fetchDeletedTasks(companyId, taskFilters),
        fetchCompanyUsers(companyId).catch(() => []),
      ]);
      let nextFunnelNames = {};
      let nextStageNames = {};
      try {
        const enriched = await enrichOpportunitiesFunnelStageNames(companyId, opps);
        nextFunnelNames = enriched.funnelNameById || {};
        nextStageNames = enriched.stageNameByFunnelStage || {};
      } catch {
        nextFunnelNames = {};
        nextStageNames = {};
      }
      setFunnelNameById(nextFunnelNames);
      setStageNameByFunnelStage(nextStageNames);
      const nextUsersById = {};
      const nextUsersByUserId = {};
      users.forEach((u) => {
        if (u?.id != null && u?.name) {
          nextUsersById[String(u.id)] = String(u.name);
        }
        if (u?.userId != null && u?.name) {
          nextUsersByUserId[String(u.userId)] = String(u.name);
        }
      });
      setOpportunities(opps);
      setTasks(tks);
      setUsersById(nextUsersById);
      setUsersByUserId(nextUsersByUserId);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.message || e?.message;
      setError(
        status
          ? `Não foi possível carregar itens apagados (${status}). ${detail || ""}`
          : "Não foi possível carregar itens apagados."
      );
      setOpportunities([]);
      setTasks([]);
      setUsersById({});
      setUsersByUserId({});
      setFunnelNameById({});
      setStageNameByFunnelStage({});
    } finally {
      setLoading(false);
    }
  }, [companyId, buildOppFilters, taskOpportunityId]);

  const formatUserRef = useCallback(
    (ref) => {
      if (!ref || String(ref).trim() === "") return null;
      const raw = String(ref).trim();
      const nameByDbId = usersById[raw];
      if (nameByDbId) return `${nameByDbId} (${raw})`;
      const nameByFirebaseUid = usersByUserId[raw];
      if (nameByFirebaseUid) return `${nameByFirebaseUid} (${raw})`;
      return raw;
    },
    [usersById, usersByUserId]
  );

  const formatResponsible = useCallback(
    (id) => {
      if (!id) return null;
      return formatUserRef(id);
    },
    [formatUserRef]
  );

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      setOpportunities([]);
      setTasks([]);
      setFunnelNameById({});
      setStageNameByFunnelStage({});
      return;
    }
    void load();
    // Recarrega com filtros atuais só ao trocar empresa; demais vezes use "Aplicar filtros".
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load muda a cada tecla nos filtros
  }, [companyId]);

  const handleRestore = async (itemId) => {
    setMessage("");
    setError("");
    setRestoringId(itemId);
    try {
      await restoreOpportunity(companyId, itemId);
      setOpportunities((prev) => prev.filter((item) => item.id !== itemId));
      setMessage("Oportunidade restaurada com sucesso.");
    } catch (e) {
      const status = e?.response?.status;
      const apiMsg = e?.response?.data?.message;
      setMessage("");
      setError(
        apiMsg
          ? String(apiMsg)
          : status === 404
            ? "Oportunidade não encontrada no histórico ou já restaurada."
            : `Falha ao restaurar (${status || "erro"}).`
      );
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <>
      <section className="card">
        <div className="section-header">
          <h2>Oportunidades apagadas</h2>
          <span className="section-badge">{companyName}</span>
        </div>

        <p className="section-description">
          Somente oportunidades cuja última revisão no sistema é exclusão (Envers). Use o cabeçalho da
          empresa selecionada para consultar o tenant correto. Os filtros abaixo são aplicados na API.
        </p>

        <div
          className="deleted-filters"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "0.5rem 0.75rem",
            marginBottom: "0.75rem",
            alignItems: "end",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span className="muted-text" style={{ fontSize: "0.85rem" }}>
              Título (contém)
            </span>
            <input
              className="search-input"
              placeholder="Buscar por título..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span className="muted-text" style={{ fontSize: "0.85rem" }}>
              Excluída a partir de
            </span>
            <input
              className="search-input"
              type="datetime-local"
              value={fromLocal}
              onChange={(e) => setFromLocal(e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span className="muted-text" style={{ fontSize: "0.85rem" }}>
              Excluída até
            </span>
            <input
              className="search-input"
              type="datetime-local"
              value={toLocal}
              onChange={(e) => setToLocal(e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span className="muted-text" style={{ fontSize: "0.85rem" }}>
              Responsável (id exato)
            </span>
            <input
              className="search-input"
              placeholder="responsible_id"
              value={responsibleId}
              onChange={(e) => setResponsibleId(e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span className="muted-text" style={{ fontSize: "0.85rem" }}>
              Quem excluiu (contém)
            </span>
            <input
              className="search-input"
              placeholder="revinfo.updated_by"
              value={deletedBy}
              onChange={(e) => setDeletedBy(e.target.value)}
            />
          </label>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button className="secondary-button" type="button" onClick={() => load()} disabled={loading}>
              {loading ? "Carregando..." : "Aplicar filtros"}
            </button>
          </div>
        </div>

        {message && <div className="success-box">{message}</div>}
        {error && <div className="error-box">{error}</div>}

        {loading ? (
          <p className="muted-text">Carregando oportunidades apagadas...</p>
        ) : !opportunities.length ? (
          <p className="muted-text">Nenhuma oportunidade apagada encontrada.</p>
        ) : (
          <div className="action-list">
            {opportunities.map((item) => {
              const { funnelName, stageName } = resolveFunnelStageLabels(
                item,
                funnelNameById,
                stageNameByFunnelStage
              );
              return (
              <article key={item.id} className="action-item">
                <div>
                  <strong>{item.title || "(sem título)"}</strong>
                  <p className="muted-text">
                    Removida em {formatDeletedAt(item.deletedAt)}
                  </p>
                  <p className="muted-text">
                    Quem apagou: {formatUserRef(item.deletedBy) || formatDeletedBy(item.deletedBy)}
                  </p>
                  {item.responsibleId && (
                    <p className="muted-text">Responsável: {formatResponsible(item.responsibleId)}</p>
                  )}
                  {item.funnelId && (
                    <p className="muted-text">
                      Funil: {funnelName ? (
                        <>
                          <strong>{funnelName}</strong>
                          <span> · </span>
                        </>
                      ) : null}
                      <span style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.9em" }}>
                        {item.funnelId}
                      </span>
                    </p>
                  )}
                  {item.stageId && (
                    <p className="muted-text">
                      Estágio: {stageName ? (
                        <>
                          <strong>{stageName}</strong>
                          <span> · </span>
                        </>
                      ) : null}
                      <span style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.9em" }}>
                        {item.stageId}
                      </span>
                    </p>
                  )}
                  {formatDeleteEndpoint(item.deleteRequestEndpoint) && (
                    <p className="muted-text">
                      endpoint: {formatDeleteEndpoint(item.deleteRequestEndpoint)}
                    </p>
                  )}
                </div>
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => handleRestore(item.id)}
                  disabled={restoringId === item.id}
                >
                  {restoringId === item.id ? "Restaurando..." : "Restaurar"}
                </button>
              </article>
            );
            })}
          </div>
        )}
      </section>

      <section className="card" style={{ marginTop: "1rem" }}>
        <div className="section-header">
          <h2>Tarefas apagadas</h2>
          <span className="section-badge">{companyName}</span>
        </div>

        <p className="section-description">
          Tarefas com última revisão de exclusão. Usa os mesmos filtros da seção acima, mais o id da
          oportunidade (quando existir). A restauração de tarefas ainda não está exposta nesta API.
        </p>

        <div style={{ marginBottom: "0.75rem" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem", maxWidth: "320px" }}>
            <span className="muted-text" style={{ fontSize: "0.85rem" }}>
              Oportunidade (id ou external)
            </span>
            <input
              className="search-input"
              placeholder="UUID ou opportunity_id_external"
              value={taskOpportunityId}
              onChange={(e) => setTaskOpportunityId(e.target.value)}
            />
          </label>
        </div>

        {loading ? (
          <p className="muted-text">Carregando tarefas apagadas...</p>
        ) : !tasks.length ? (
          <p className="muted-text">Nenhuma tarefa apagada encontrada.</p>
        ) : (
          <div className="action-list">
            {tasks.map((item) => (
              <article key={item.id} className="action-item">
                <div>
                  <strong>{item.title || "(sem título)"}</strong>
                  <p className="muted-text">
                    Removida em {formatDeletedAt(item.deletedAt)}
                  </p>
                  <p className="muted-text">
                    Quem apagou: {formatUserRef(item.deletedBy) || formatDeletedBy(item.deletedBy)}
                  </p>
                  {item.responsibleId && (
                    <p className="muted-text">Responsável: {formatResponsible(item.responsibleId)}</p>
                  )}
                  {item.opportunityId && (
                    <p className="muted-text">Oportunidade: {item.opportunityId}</p>
                  )}
                  {formatDeleteEndpoint(item.deleteRequestEndpoint) && (
                    <p className="muted-text">
                      endpoint: {formatDeleteEndpoint(item.deleteRequestEndpoint)}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
